// Help > Report a problem / Feedback. The user writes what happened; the
// details support needs to reproduce it are added automatically and shown,
// so nothing is sent that the user cannot see. Nothing is sent by the
// platform itself: the user copies the text or opens it in their own e-mail
// (desktop program, Gmail or Outlook on the web), so it also works for users
// whose computer has no e-mail program set up.
import { useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, TextField, Tooltip, Typography,
} from "@mui/material";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import { useLanguage } from "../context/LanguageContext";
import { metenApi } from "../services/metenApi";
import { REPORT_KINDS, buildProblemReport, gmailUrl, mailtoUrl, outlookUrl } from "../utils/problemReport";

const MIN_DESCRIPTION = 5;

async function copyText(text) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to the textarea fallback
  }
  try {
    const area = document.createElement("textarea");
    area.value = text;
    area.setAttribute("readonly", "");
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(area);
    return ok;
  } catch {
    return false;
  }
}

export default function ReportProblemDialog({ open, onClose, context = {}, supportEmail }) {
  const { t, isGreek } = useLanguage();
  const [kind, setKind] = useState("problem");
  const [description, setDescription] = useState("");
  const [run, setRun] = useState(null);
  const [notice, setNotice] = useState(null); // { severity, text }

  const projectId = context.projectId || null;

  useEffect(() => {
    if (!open) return undefined;
    setNotice(null);
    setRun(null);
    if (!projectId) return undefined;
    let alive = true;
    // Read-only: the same status the Simulation page shows. Any failure
    // (no feasibility access, network) just leaves the line out.
    Promise.resolve()
      .then(() => metenApi.getFeasibilityStatus(String(projectId)))
      .then((snapshot) => { if (alive) setRun(snapshot || null); })
      .catch(() => {});
    return () => { alive = false; };
  }, [open, projectId]);

  const report = useMemo(
    () => buildProblemReport({ kind, description, context, run, isGreek }),
    [kind, description, context, run, isGreek],
  );
  const ready = description.trim().length >= MIN_DESCRIPTION;

  const handleCopy = async () => {
    const ok = await copyText(`${report.subject}\n\n${report.body}`);
    setNotice(ok
      ? { severity: "success", text: t("report.copied", { email: supportEmail }) }
      : { severity: "warning", text: t("report.copyFailed") });
  };
  const openUrl = (url) => window.open(url, "_blank", "noopener,noreferrer");
  const handleClose = () => {
    onClose?.();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth data-no-translate="">
      <DialogTitle sx={{ pb: 1 }}>{t("report.title")}</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{t("report.intro")}</Typography>
        <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} role="group" aria-label={t("report.kindLabel")}>
          {REPORT_KINDS.map((k) => (
            <Chip
              key={k}
              label={t(`report.kind.${k}`)}
              color={kind === k ? "primary" : "default"}
              variant={kind === k ? "filled" : "outlined"}
              onClick={() => setKind(k)}
              aria-pressed={kind === k}
            />
          ))}
        </Stack>
        <TextField
          fullWidth
          multiline
          minRows={4}
          autoFocus
          label={t(`report.descriptionLabel.${kind}`)}
          placeholder={t(`report.descriptionPlaceholder.${kind}`)}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          inputProps={{ maxLength: 4000 }}
        />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2, mb: 0.5 }}>{t("report.detailsTitle")}</Typography>
        <Box
          component="pre"
          data-testid="report-details"
          sx={{
            m: 0, p: 1.25, borderRadius: 1.5, fontSize: 12, lineHeight: 1.5, whiteSpace: "pre-wrap", overflowWrap: "anywhere",
            maxHeight: 180, overflowY: "auto", fontFamily: "inherit",
            backgroundColor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)"),
          }}
        >
          {report.details}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
          {t("report.screenshotHint")}
        </Typography>
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 1.5 }}>
          <Typography variant="body2">
            {t("report.supportEmail")}: <b>{supportEmail}</b>
          </Typography>
          <Tooltip title={t("report.copyEmail")}>
            <IconButton size="small" aria-label={t("report.copyEmail")} onClick={async () => {
              const ok = await copyText(supportEmail);
              setNotice(ok ? { severity: "success", text: t("report.emailCopied") } : { severity: "warning", text: t("report.copyFailed") });
            }}>
              <ContentCopyOutlinedIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="body2" color="text.secondary">{t("report.webmail")}</Typography>
          <Button size="small" disabled={!ready} onClick={() => openUrl(gmailUrl(supportEmail, report))}>Gmail</Button>
          <Button size="small" disabled={!ready} onClick={() => openUrl(outlookUrl(supportEmail, report))}>Outlook</Button>
        </Stack>
        {!ready ? (
          <Typography variant="caption" color="text.secondary">{t("report.needDescription")}</Typography>
        ) : null}
        {notice ? <Alert severity={notice.severity} sx={{ mt: 1.5 }} onClose={() => setNotice(null)}>{notice.text}</Alert> : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, flexWrap: "wrap", gap: 1 }}>
        <Button onClick={handleClose}>{t("report.close")}</Button>
        <Box sx={{ flex: 1 }} />
        <Button variant="outlined" startIcon={<ContentCopyOutlinedIcon />} disabled={!ready} onClick={handleCopy}>
          {t("report.copy")}
        </Button>
        <Button variant="contained" startIcon={<EmailOutlinedIcon />} disabled={!ready}
          onClick={() => { window.location.href = mailtoUrl(supportEmail, report); }}>
          {t("report.openEmail")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
